import React, { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";

//Common
import { LoadingIndicator } from "../../common/VFX-UI";

//App
import County from "./County";
import countyApi from "../../../app/model/api/countyApi";
import stateApi from "../../../app/model/api/stateApi";
import { queryKeys } from "./constants";
import { queryKeys as stateQueryKeys } from "../States/constants";
import { FormikTypeaheadOption, SortBy } from "../../../types";
import { CountyContextData, CountyState } from "../../../types/county";
import {
  EMPTY_GUID,
  GRID_PAGE_SIZE,
} from "../../../utility/constants/constants";
import {
  selectIsActive,
  setIsActive,
  selectSearchValue,
  setSearchValue,
} from "../../../store/county";
import { SearchValue } from "../../../store/types";

export const CountyContext = React.createContext<CountyContextData | null>(
  null
);

const CountyContainer = () => {
  const dispatch = useDispatch();
  const [state, setState] = useState<CountyState>({
    stateId: null,
    countyId: null,
    activeToggle: useSelector(selectIsActive) ?? true,
    searchPayload: useSelector(selectSearchValue) ?? null,
    pageSize: GRID_PAGE_SIZE,
    currentPage: 1,
    sortBy: [{ field: "name", direction: 1 }],
  });

  const statesQuery = useQuery({
    queryKey: [stateQueryKeys.getStates],
    queryFn: () => stateApi.getAll({ isActive: true }),
    select: (data) => {
      const transformedData = data?.result?.map((state) => ({
        label: state.name as string,
        value: state.id as string,
      }));
      return transformedData as FormikTypeaheadOption[];
    },
  });

  const countiesGridQuery = useQuery({
    queryKey: [queryKeys.getCounties, state.stateId],
    queryFn: () =>
      countyApi.searchCounties({
        ...state.searchPayload,
        stateId: state.stateId ?? EMPTY_GUID,
        isActive: state.activeToggle,
        pageSize: state.pageSize,
        pageNumber: state.currentPage,
        sortBy: state.sortBy,
      }),
  });

  const countyQuery = useQuery({
    queryKey: [queryKeys.getCounty, state.countyId],
    queryFn: () => countyApi.get(state.countyId ?? EMPTY_GUID),
    enabled: state.countyId !== null,
  });

  const updateCountyId = async (countyId: string | null) => {
    setState((prevState) => ({
      ...prevState,
      countyId: countyId,
    }));
  };

  const updateStateId = async (stateId: string | null) => {
    setState((prevState) => ({
      ...prevState,
      searchPayload: null,
      stateId: stateId,
    }));
  };

  const updateActiveToggle = async () => {
    dispatch(setIsActive());
    setState((prevState) => ({
      ...prevState,
      activeToggle: !prevState.activeToggle,
    }));
  };

  const updateSortBy = async (sortBy: SortBy) => {
    const { field, direction } = sortBy;
    setState((prevState) => ({
      ...prevState,
      sortBy: [{ field, direction }],
    }));
  };

  const updateSearchPayload = async (values: SearchValue | null) => {
    if (values?.name) {
      dispatch(setSearchValue({ name: values.name }));
    }
    if (values && Object.keys(values).length) {
      setState((prevState) => ({
        ...prevState,
        searchPayload: values,
        currentPage: 1,
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        searchPayload: null,
        currentPage: 1,
        sortBy: [{ field: "name", direction: 1 }],
      }));
    }
  };

  const updateCurrentPage = async (currentPage: number) => {
    setState((prevState) => ({
      ...prevState,
      currentPage: currentPage,
    }));
  };

  const updateCurrentPageSize = async (pageSize: number) => {
    setState((prevState) => ({
      ...prevState,
      pageSize: pageSize,
    }));
  };

  const { mutate: deleteMutation } = useMutation<void, Error, string>({
    mutationFn: async (value: string) => {
      await countyApi.delete(value);
    },
  });

  const { mutate: activateMutation } = useMutation<void, Error, string>({
    mutationFn: async (value: string) => {
      await countyApi.activate(value);
    },
  });

  const contextValue: CountyContextData = {
    data: countyQuery?.data?.result,
    id: state.countyId,
    setId: updateCountyId,
    states: statesQuery?.data,
    stateId: state.stateId,
    setStateId: updateStateId,
    activeToggle: state.activeToggle,
    setActiveToggleValue: updateActiveToggle,
    searchValue: state.searchPayload ?? undefined,
    setSearchValue: updateSearchPayload,
    paging: {
      currentPage: state.currentPage,
      totalSize: countiesGridQuery?.data?.totalSize ?? 0,
    },
    pageSize: state.pageSize,
    setCurrentPageSize: updateCurrentPageSize,
    setCurrentPage: updateCurrentPage,
    setSortBy: updateSortBy,
    refetchGrid: () => countiesGridQuery?.refetch(),
  };

  if (statesQuery?.isLoading) return <LoadingIndicator />;

  return (
    <CountyContext.Provider value={contextValue}>
      <County
        data={countiesGridQuery?.data?.result}
        deleteMutation={deleteMutation}
        activateMutation={activateMutation}
      />
    </CountyContext.Provider>
  );
};

export default CountyContainer;
